import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import { Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ProductsFormDialog from './ProductsFormDialog';
import { PRODUCTS_GET_ENDPOINT } from '../../config/urls';
import { useAuth } from '../../hooks/AuthProvider';
import { ColorsConstants } from '../../cssUtils/constants';
import typography from '../../cssUtils/typography';
import AppTable from '../../common/app-table';
import TableSearchInput from '../TableSearchInput';

export default function ProductsTable({ refreshKey }) {
  const { getValidToken } = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [productModalState, setProductModalState] = useState({ 
    isOpen: false, 
    selectedClient: null, 
    mode: 'add', 
    jsonData: '' 
  });
  
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20
  });

  const [productsData, setProductsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleAddProductButton = () => {
    setProductModalState({ isOpen: true, selectedClient: null, mode: 'add_product', jsonData: '' });
  };

  const fetchData = async () => {
    // Construct query parameters for server-side filtering and pagination
    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.page * controller.rowsPerPage).toString(),
      search_query: searchQuery
    });

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    try {
      const response = await fetch(`${PRODUCTS_GET_ENDPOINT}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const jsonData = await response.json();

      console.log("GET API response: ", jsonData);

      setProductsData(jsonData.results);
      setRowCount(jsonData.count);
      
    } catch (error) {
      console.error('Error fetching data:', error.message);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [
    controller.page, 
    controller.rowsPerPage, 
    refreshKey,
    searchQuery
  ]);

  // pagination
  const handlePageChange = (event, newPage) => {
    setController(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0
    });
  };

  const handleEditClick = (product) => {
    setProductModalState({ isOpen: true, selectedClient: product, mode: 'update_product' });
  };

  const handleSearchQueryChange = (value) => {
    if (value.length > 50) return;
    setSearchQuery(value);
    setCharCount(value.length);
    setController(prev => ({
      ...prev,
      page: 0
    }));
  };

  // Define table columns for AppTable
  const tableColumns = [
    {
      id: "content_id",
      label: "Content ID",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          {row.content_id}
        </Typography>
      ),
    },
    {
  id: "title",
  label: "Title",
  render: (row) => (
    <Tooltip title={row.title || "N/A"} arrow>
      <Typography
        sx={{
          ...typography.bodyLarge,
          maxWidth: 250,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: "#1D2330",
          cursor: "pointer",
        }}
      >
        {row.title || "N/A"}
      </Typography>
    </Tooltip>
  ),
},
  {
  id: "description",
  label: "Description",
  render: (row) => {
    const plainText = stripHtml(row.description) || "N/A";
    return (
      <Tooltip title={plainText} arrow>
        <Typography
          sx={{
            ...typography.bodyLarge,
            maxWidth: 300,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "#1D2330",
            cursor: "pointer",
          }}
        >
          {plainText}
        </Typography>
      </Tooltip>
    );
  },
},
    {
      id: "item_price",
      label: "Item Price",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
          ₹{row.item_price}
        </Typography>
      ),
    },
    {
      id: "sale_price",
      label: "Sale Price",
      render: (row) => (
      <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
      {row.sale_price != null ? `₹${row.sale_price}` : "-"}
     </Typography>
     ),
    },
    {
      id: "status",
      label: "Status",
      render: (row) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: "16px",
            backgroundColor:
              row.status === "active" ? "#E8F5E9" : "#FFEBEE",
            color:
              row.status === "active" ? "#2E7D32" : "#C62828",
            ...typography.bodySmall,
            fontWeight: 500,
          }}
        >
          {row.status === "active"
            ? "Active"
            : row.status === "inactive"
            ? "Inactive"
            : "Unknown"}
        </Box>
      ),
    },
    {
      id: "availability",
      label: "Availability",
      render: (row) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: "16px",
            backgroundColor:
              row.availability === "in_stock"
                ? "#E8F5E9"
                : row.availability === "out_of_stock"
                ? "#FFEBEE"
                : row.availability === "coming_soon"
                ? "#FFF3E0"
                : "#F5F5F5",
            color:
              row.availability === "in_stock"
                ? "#2E7D32"
                : row.availability === "out_of_stock"
                ? "#C62828"
                : row.availability === "coming_soon"
                ? "#ED6C02"
                : "#616161",
            ...typography.bodySmall,
            fontWeight: 500,
          }}
        >
          {row.availability === "in_stock"
            ? "In Stock"
            : row.availability === "out_of_stock"
            ? "Out of Stock"
            : row.availability === "coming_soon"
            ? "Coming Soon"
            : row.availability === "discontinued"
            ? "Discontinued"
            : "Unknown"}
        </Box>
      ),
    },
    {
      id: "action",
      label: "Action",
      render: (row) => (
        <Tooltip title="Edit" placement="top" arrow>
  <IconButton
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      handleEditClick(row);
    }}
    sx={{
      color: "#1D2330",
      borderRadius: "6px",
      "&:hover": {
        bgcolor: "#F0F2F8",
        color: "#1D2330",
      },
    }}
  >
    <EditIcon sx={{ fontSize: 18 }} />
  </IconButton>
</Tooltip>
      ),
    },
  ];
// Updated helper — also strips literal \r\n / \n sequences and collapses whitespace
 const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const text = doc.body.textContent || "";
  return text
    .replace(/\\r\\n|\\n|\\r/g, " ") // remove literal backslash-n / backslash-r sequences
    .replace(/\s+/g, " ")            // collapse multiple spaces
    .trim();
};

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC",
    },
    "& td": {
      borderBottom: "none",
      borderTop: "none",
    },
  });

  return (
    <Box>
      {/* Header Section with Search and Add Button */}
      <Box
        sx={{
          mb: 3,
          pt: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Search - Takes remaining width */}
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => handleSearchQueryChange(e.target.value)}
            charCount={charCount}
            tooltip="Search by product title"
            placeholder="Search products by title..."
          />
        </Box>

        {/* Add Product Button - Fixed width */}
        <Box sx={{ flex: "0 0 auto" }}>
          <Button
            variant="contained"
            onClick={handleAddProductButton}
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: "40px",
              px: 3,
              ...typography.bodyLarge,
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "8px",
              backgroundColor: ColorsConstants.primaryBlue100,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: ColorsConstants.primaryBlue100,
                opacity: 0.9,
              },
            }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Table using AppTable */}
      <AppTable
        columns={tableColumns}
        rows={productsData}
        rowKey={(row, index) => index}
        maxHeight={540}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F3F6FB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C5CEDB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#A8B4C7",
          },
        }}
        rowSx={rowSx}
        paperSx={{
          border: "1px solid #DCDFE5",
          borderRadius: "12px",
          mb: 2,
        }}
        emptyMessage="No products found"
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      
      <ProductsFormDialog
        modalState={productModalState}
        handleModalState={setProductModalState}
        selectedClient={productModalState.selectedClient}
        productsData={productsData}
        fetchData={fetchData}
      />
    </Box>
  );
}